import { logDebug, logInfo } from './logger';
import { getCorrelationContext } from './correlation';
import { getCurrentRouteContext } from './route-context';

import type { LevelTwoConfig } from '@/feature-flags/types/variant.types';
import { LEVEL_TWO_FEATURES, type LevelTwoFeatureKey } from '@/feature-flags/consts/level-two-features.const';

export interface FeatureAccessCheck {
  feature: LevelTwoFeatureKey;
  hasAccess: boolean;
  reason: 'feature_enabled' | 'feature_disabled';
}

export interface FeatureAccessContext {
  route?: string;
  source?: string;
  variant?: string | null;
}

export function hasFeatureAccess(
  feature: LevelTwoFeatureKey,
  config: LevelTwoConfig,
): boolean {
  const featureMeta = LEVEL_TWO_FEATURES[feature];

  if (!featureMeta) {
    logDebug('Feature not found in metadata', { feature });
    return false;
  }

  const { configKey } = featureMeta;
  return config[configKey] === true;
}

export function checkFeatureAccess(
  feature: LevelTwoFeatureKey,
  config: LevelTwoConfig,
  userId?: string,
  context?: FeatureAccessContext,
): FeatureAccessCheck {
  const hasAccess = hasFeatureAccess(feature, config);
  const routeContext = getCurrentRouteContext();

  const result: FeatureAccessCheck = {
    feature,
    hasAccess,
    reason: hasAccess ? 'feature_enabled' : 'feature_disabled',
  };

  logInfo('Feature access evaluated', {
    event: 'feature_access_evaluated',
    ...getCorrelationContext(),
    feature,
    hasAccess,
    reason: result.reason,
    userId,
    configValue: config[LEVEL_TWO_FEATURES[feature].configKey],
    route: context?.route || routeContext.route,
    source: context?.source || 'checkFeatureAccess',
    variant: context?.variant,
  });

  return result;
}

export function getAccessibleFeatures(
  config: LevelTwoConfig,
): LevelTwoFeatureKey[] {
  return (Object.keys(LEVEL_TWO_FEATURES) as LevelTwoFeatureKey[]).filter(
    (feature) => hasFeatureAccess(feature, config),
  );
}
